/**
 * TaskArtefactUploader — 3-step presigned-PUT upload for Task Artefact.
 *
 * Step 1: POST /api/tests/:id/task-artefact-upload-url → upload_url + artefact_key
 * Step 2: PUT file directly to R2 at upload_url (Invariant 7 — not proxied)
 * Step 3: POST /api/tests/:id/task-artefact-confirm → task_artifact_url set on Test
 *
 * Owned by: test-composer-dev.
 */

import { useRef, useState, type ReactElement } from 'react';
import {
  confirmTaskArtefact,
  getTaskArtefactUploadUrl,
} from '../../state/tests/api.ts';

/** Allowed MIME types for task artefacts (mirrors route-level validation). */
const TASK_ARTEFACT_MIME = 'application/octet-stream,application/zip,application/pdf';
/** 50 MB max (test_task scope limit in scopes.ts). */
const MAX_BYTES = 50 * 1024 * 1024;

export interface TaskArtefactUploaderProps {
  testId: string;
  /** Called with the new signed download URL when upload + confirm succeed. */
  onUploaded: (task_artifact_url: string) => void;
}

export function TaskArtefactUploader(
  props: TaskArtefactUploaderProps,
): ReactElement {
  const { testId, onUploaded } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const handleClick = (): void => {
    if (inputRef.current) inputRef.current.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setInfo(null);
    if (file.size > MAX_BYTES) {
      setError(
        `File too large: ${String(Math.round(file.size / (1024 * 1024)))} MB (max 50 MB).`,
      );
      return;
    }
    setBusy(true);
    void (async () => {
      try {
        // Step 1: get presigned PUT URL
        const prep = await getTaskArtefactUploadUrl(
          testId,
          file.name,
          file.type || 'application/octet-stream',
          file.size,
        );
        if (!prep.ok) {
          setError(`${prep.error.code}: ${prep.error.message}`);
          return;
        }
        const { upload_url, artefact_key } = prep.data;

        // Step 2: PUT directly to R2 (Invariant 7 — not proxied through API)
        const putRes = await fetch(upload_url, {
          method: 'PUT',
          headers: { 'content-type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!putRes.ok) {
          setError(`R2 upload failed: ${String(putRes.status)} ${putRes.statusText}`);
          return;
        }

        // Step 3: confirm
        const confirm = await confirmTaskArtefact(testId, artefact_key);
        if (!confirm.ok) {
          setError(`${confirm.error.code}: ${confirm.error.message}`);
          return;
        }
        setInfo(`Uploaded: ${file.name}`);
        onUploaded(confirm.data.task_artifact_url);
      } finally {
        setBusy(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    })();
  };

  return (
    <div className="task-artefact-uploader">
      <input
        type="file"
        ref={inputRef}
        accept={TASK_ARTEFACT_MIME}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <button type="button" onClick={handleClick} disabled={busy}>
        {busy ? 'Uploading…' : 'Upload Task Artefact (.rvt, .zip, .pdf)'}
      </button>
      {error ? <p className="upload-error">{error}</p> : null}
      {info ? <p className="upload-info">{info}</p> : null}
    </div>
  );
}
